import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUnreadCount } from 'src/common/ducks/user';
import useUnreadCount from 'src/common/hooks/useUnreadCount';
import { getUser } from 'src/common/utils/reduxStoreSelectors';
import { PATHS } from '../../constants';
import UnreadIndicator from '../UnreadIndicator';
import NavItem from './NavItem';

const MAX_DISPLAYED_UNREAD_COUNT = 99;

type InboxNavItemProps = {
  classNameModifier?: string;
};

const InboxNavItem: FC<InboxNavItemProps> = ({ classNameModifier }) => {
  const dispatch = useDispatch();
  const user = useSelector(getUser);
  const unreadCount = user.get('unread_messages_count') ?? 0;
  const unreadCountLabel = unreadCount > MAX_DISPLAYED_UNREAD_COUNT
    ? `${MAX_DISPLAYED_UNREAD_COUNT}+`
    : String(unreadCount);
  const unreadIndicatorModifier = unreadCount > MAX_DISPLAYED_UNREAD_COUNT
    ? 'inNav cappedCount'
    : 'inNav';

  const handleUnreadCount = useCallback(
    (count: number) => dispatch(setUnreadCount(count)),
    [dispatch],
  );
  useUnreadCount(!!user.get('id'), handleUnreadCount);

  return (
    <NavItem
      classNameModifier={classNameModifier}
      to={PATHS.INBOX}
      icon="envelope"
      indicator={unreadCount > 0 ? (
        <UnreadIndicator
          classNameModifier={unreadIndicatorModifier}
          /*
           * UX: We always show an unread indicator when there are unread messages,
           * but only render a numeric badge when there is more than one unread.
           * Passing `undefined` for `unreadCount` intentionally signals to
           * `UnreadIndicator` to render a dot / generic badge without a number,
           * so a single unread message shows an unnumbered badge rather than "1".
           */
          unreadCountLabel={unreadCount > 1 ? unreadCountLabel : undefined}
        />
      ) : undefined}
    >
      Inbox
    </NavItem>
  );
};

export default InboxNavItem;
